import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const INDIAN_CITIES = [
  "Chandigarh Tricity", "Ahmedabad", "Delhi-NCR", "Mumbai", "Bengaluru", "Pune",
  "Hyderabad", "Chennai", "Kolkata", "Jaipur", "Lucknow", "Indore",
  "Surat", "Nagpur", "Coimbatore", "Kochi", "Vadodara", "Bhopal",
  "Ludhiana", "Other",
];

export default function VisitCircle() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "Chandigarh Tricity",
    business_name: "", business_type: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/visit-circle", form);
      setDone(true);
      toast.success("Thanks, our team will reach out soon.");
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="visit-circle-page" className="bg-white">
      {/* HERO */}
      <section className="bg-navy text-white py-20 lg:py-28">
        <div className="container-narrow">
          <div className="overline text-gold mb-4">Visit a Circle</div>
          <h1 className="text-4xl sm:text-6xl font-display font-semibold leading-[1.05] max-w-4xl">
            Spend a morning inside a D32 India Circle.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-gray-300">
            Meet the community, experience the playbook, and see if D32 is
            right for you.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="container-narrow grid lg:grid-cols-2 gap-16 items-start">
          {/* Value prop */}
          <div>
            <div className="overline text-gold mb-3">What to expect</div>
            <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy">
              See How D32 Works, Without the Pressure.
            </h2>
            <ul className="mt-8 space-y-5">
              {[
                ["A warm welcome", "Greet our members, meet your potential referral partners."],
                ["Guest speaker session", "Learn from a business leader who has been through it, not a guru."],
                ["Structured networking", "Curated 1:1s, not random card-swapping."],
                ["Honest Q&A", "Ask anything. Then take 48 hours to decide."],
              ].map(([t, d]) => (
                <li key={t} className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-full bg-gold/15 flex items-center justify-center text-navy flex-shrink-0">
                    <Check size={16} />
                  </div>
                  <div>
                    <div className="font-display text-lg text-navy">{t}</div>
                    <div className="text-sm text-gray-600 mt-0.5">{d}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div
              className="mt-10 aspect-[5/3] rounded-2xl bg-cover bg-center shadow-lg hidden lg:block"
              style={{
                backgroundImage:
                  "url(https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)",
              }}
            />
          </div>

          {/* FORM */}
          <div className="bg-cream rounded-3xl p-8 lg:p-10 border border-gray-200">
            {done ? (
              <div data-testid="visit-form-success" className="py-16 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-gold/20 flex items-center justify-center mb-6">
                  <Check size={28} className="text-navy" />
                </div>
                <h3 className="font-display text-3xl text-navy">You're on the list.</h3>
                <p className="mt-3 text-gray-600 max-w-sm mx-auto">
                  Our chapter chair will reach out within 24 hours with the next
                  meeting details for {form.city}.
                </p>
              </div>
            ) : (
              <form data-testid="visit-circle-form" onSubmit={onSubmit} className="space-y-5">
                <h3 className="font-display text-2xl text-navy">
                  Tell us a bit about you
                </h3>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full name *</Label>
                    <Input
                      id="name"
                      required
                      data-testid="visit-input-name"
                      value={form.name}
                      onChange={update("name")}
                      className="mt-1.5 h-11 bg-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      required
                      data-testid="visit-input-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      className="mt-1.5 h-11 bg-white"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    data-testid="visit-input-email"
                    value={form.email}
                    onChange={update("email")}
                    className="mt-1.5 h-11 bg-white"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Select value={form.city} onValueChange={(v) => setForm((f) => ({ ...f, city: v }))}>
                      <SelectTrigger
                        id="city"
                        data-testid="visit-input-city"
                        className="mt-1.5 h-11 bg-white"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INDIAN_CITIES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="business_type">Business / Industry</Label>
                    <Input
                      id="business_type"
                      data-testid="visit-input-business-type"
                      value={form.business_type}
                      onChange={update("business_type")}
                      className="mt-1.5 h-11 bg-white"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="business_name">Business name</Label>
                  <Input
                    id="business_name"
                    data-testid="visit-input-business-name"
                    value={form.business_name}
                    onChange={update("business_name")}
                    className="mt-1.5 h-11 bg-white"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Anything you'd like us to know?</Label>
                  <Textarea
                    id="message"
                    data-testid="visit-input-message"
                    value={form.message}
                    onChange={update("message")}
                    className="mt-1.5 bg-white"
                    rows={4}
                  />
                </div>

                {error && (
                  <div data-testid="visit-form-error" className="text-sm text-red-600">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="visit-form-submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold py-4 rounded-full hover:bg-navy-deep hover:-translate-y-0.5 transition disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                  Request my seat
                </button>
                <p className="text-[11px] text-gray-500 text-center">
                  Your details stay private. No spam, ever.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* MEMBER TESTIMONIALS */}
      <section className="bg-navy text-white py-20 lg:py-24">
        <div className="container-narrow">
          <div className="overline text-gold mb-4">From members who visited first</div>
          <h2 className="text-3xl sm:text-4xl font-display font-semibold leading-tight max-w-2xl">
            One meeting was all it took.
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              {
                quote:
                  "Walked in as a skeptical guest, walked out with two intros that closed inside a month. Best two hours I've spent on my business.",
                name: "Priya Sharma",
                role: "Founder, Lumen Digital · Chandigarh Tricity",
              },
              {
                quote:
                  "The room was warm, structured and serious. No forced pitching. Just the right people, doing this well for years.",
                name: "Hardik Patel",
                role: "MD, Patel Polymers · Ahmedabad",
              },
              {
                quote:
                  "I've paid consultants for less clarity than I got in one guest visit. D32 is the peer table I didn't know I needed.",
                name: "Neha Khurana",
                role: "Founder, Khurana HR Solutions · Chandigarh Tricity",
              },
            ].map((t) => (
              <div
                key={t.name}
                className="border border-white/15 rounded-2xl p-6 bg-white/[0.02] hover:border-gold/60 transition"
              >
                <div className="text-gold text-2xl font-display leading-none">"</div>
                <p className="mt-1 text-gray-200 text-sm leading-relaxed">{t.quote}</p>
                <div className="mt-5 text-xs">
                  <div className="font-semibold text-white">{t.name}</div>
                  <div className="text-gray-400">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
